/**
 * FuelTariffConfiguration - Fuel cost configuration component
 * 
 * Handles natural gas and diesel fuel tariff inputs for generators.
 * Located in Utility tab under Fuel sub-tab.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Alert,
    Button,
} from '@mui/material';
import { LocalGasStation, Whatshot, Add as AddIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors, useAuth, vortxTypography } from '@vortx/shared-ui';
import GasTariffBuilder from './GasTariffBuilder';
import ApprovedGasTariffSelector from './ApprovedGasTariffSelector';
import GasTariffSummary from './GasTariffSummary';
import axios from 'axios';

/**
 * Safely get color from theme with fallback
 */
const safeColor = (color, fallback = '#00BCD4') => {
    return color || fallback;
};

/**
 * Safely apply alpha with color validation
 */
const safeAlpha = (color, opacity, fallback = '#00BCD4') => {
    const validColor = safeColor(color, fallback);
    return alpha(validColor, opacity);
};

/**
 * FuelTariffConfiguration Component
 */
function FuelTariffConfiguration({
    dieselFuelCostPerGallon,
    setDieselFuelCostPerGallon,
    naturalGasCostPerMmbtu,
    setNaturalGasCostPerMmbtu,
    vortxStyles = {},
    projectId,
    onGasTariffChange,
    initialGasTariffId = null,
    initialGasTariffData = null,
}) {
    const { currentUser } = useAuth();

    // Gas tariff state - initialize from props if available
    const [showGasTariffBuilder, setShowGasTariffBuilder] = useState(false);
    const [selectedGasTariffId, setSelectedGasTariffId] = useState(initialGasTariffId);
    const [gasTariffData, setGasTariffData] = useState(initialGasTariffData);

    // Sync local state with props when they change (e.g., when scenario loads)
    useEffect(() => {
        if (initialGasTariffId !== selectedGasTariffId) {
            setSelectedGasTariffId(initialGasTariffId);
        }
        if (initialGasTariffData !== gasTariffData) {
            setGasTariffData(initialGasTariffData);
        }
    }, [initialGasTariffId, initialGasTariffData]);

    // Build auth headers
    const getAuthHeaders = async () => {
        const headers = { 'Content-Type': 'application/json' };
        if (currentUser) {
            try {
                const token = await currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
            } catch (e) {
                console.error('Failed to get ID token:', e);
            }
        } else {
            headers['X-Dev-User-Id'] = 'tim@vortx.us';
        }
        return headers;
    };

    // Load gas tariff data when selected
    const loadGasTariff = async (tariffId) => {
        if (!tariffId) {
            setGasTariffData(null);
            return;
        }

        try {
            const headers = await getAuthHeaders();
            const response = await axios.get(
                `/api/site_dna/v1/projects/custom-rates/${tariffId}`,
                { headers }
            );

            const tariff = response.data;
            setGasTariffData(tariff);

            // Notify parent component if callback provided
            if (onGasTariffChange) {
                onGasTariffChange({
                    tariffId,
                    tariffData: tariff
                });
            }
        } catch (err) {
            console.error('Failed to load gas tariff:', err);
            alert('Failed to load gas tariff details');
        }
    };

    // Handle saving new gas tariff
    const handleSaveGasTariff = async (tariffData) => {
        try {
            const headers = await getAuthHeaders();

            // Step 1: Create the tariff
            const createResponse = await axios.post(
                '/api/site_dna/v1/projects/custom-rates',
                tariffData,
                { headers }
            );

            const createdTariff = createResponse.data;
            const tariffId = createdTariff.id;

            // Step 2: Submit for approval
            try {
                await axios.post(
                    `/api/site_dna/v1/projects/custom-rates/${tariffId}/submit`,
                    {},
                    { headers }
                );
            } catch (submitErr) {
                console.error('Failed to submit for approval:', submitErr);
                // Continue anyway - tariff was created
            }

            // Show success message
            alert(
                `Gas tariff "${tariffData.name}" created successfully!\n\n` +
                `You can use it immediately in this scenario. ` +
                `It has been submitted for admin approval to make it available to all users.`
            );

            // Select the newly created tariff
            setSelectedGasTariffId(tariffId);
            loadGasTariff(tariffId);

            // Close dialog
            setShowGasTariffBuilder(false);
        } catch (err) {
            console.error('Failed to save gas tariff:', err);
            alert('Failed to save gas tariff');
        }
    };

    // Handle gas tariff selection change
    const handleGasTariffSelect = (tariffId) => {
        setSelectedGasTariffId(tariffId);
        if (tariffId) {
            loadGasTariff(tariffId);
        } else {
            setGasTariffData(null);
            if (onGasTariffChange) {
                onGasTariffChange(null);
            }
        }
    };

    // Handle dialog close
    const handleClose = () => {
        setShowGasTariffBuilder(false);
    };

    return (
        <Box>
            <Typography variant="h6" sx={{
                fontFamily: vortxTypography.heading,
                fontWeight: 600,
                color: 'text.primary',
                mb: 2
            }}>
                Fuel Tariffs
            </Typography>

            <Alert severity="info" sx={{
                mb: 3,
                bgcolor: safeAlpha(vortxColors.teal, 0.1, '#00BCD4')
            }}>
                <Typography variant="body2">
                    Enter fuel costs for backup power generation. These rates are used to calculate operating costs
                    for diesel and natural gas generators.
                </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* Diesel Fuel Cost */}
                <Box sx={{
                    flex: '1 1 280px',
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${safeAlpha(vortxColors.gold, 0.3, '#FFD700')}`,
                    bgcolor: safeAlpha(vortxColors.gold, 0.05, '#FFD700')
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocalGasStation sx={{ color: safeColor(vortxColors.gold, '#FFD700') }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Diesel
                        </Typography>
                    </Box>
                    <TextField
                        label="Fuel Cost"
                        type="number"
                        value={dieselFuelCostPerGallon || ''}
                        onChange={(e) => setDieselFuelCostPerGallon(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/gallon</InputAdornment>,
                        }}
                        fullWidth
                        sx={vortxStyles.textField}
                        helperText="EIA avg: $3.50/gallon"
                    />
                </Box>

                {/* Natural Gas Cost - Enhanced with Gas Tariff Integration */}
                <Box sx={{
                    flex: '1 1 280px',
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${safeAlpha(vortxColors.teal, 0.3, '#00BCD4')}`,
                    bgcolor: safeAlpha(vortxColors.teal, 0.05, '#00BCD4')
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Whatshot sx={{ color: safeColor(vortxColors.teal, '#00BCD4') }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Natural Gas
                        </Typography>
                    </Box>

                    {/* Gas Tariff Selector */}
                    <Box sx={{ mb: 2 }}>
                        <ApprovedGasTariffSelector
                            value={selectedGasTariffId}
                            onChange={handleGasTariffSelect}
                            projectId={projectId}
                        />
                    </Box>

                    {/* Create Custom Tariff Button */}
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowGasTariffBuilder(true)}
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{
                                borderColor: safeColor(vortxColors.teal, '#00BCD4'),
                                color: safeColor(vortxColors.teal, '#00BCD4'),
                                '&:hover': {
                                    borderColor: safeColor(vortxColors.teal, '#00BCD4'),
                                    bgcolor: safeAlpha(vortxColors.teal, 0.1, '#00BCD4')
                                }
                            }}
                        >
                            Create Custom Gas Tariff
                        </Button>
                    </Box>

                    {/* Show tariff summary or simple rate input */}
                    {selectedGasTariffId ? (
                        <GasTariffSummary tariffId={selectedGasTariffId} />
                    ) : (
                        <TextField
                            label="Simple Gas Rate"
                            type="number"
                            value={naturalGasCostPerMmbtu || ''}
                            onChange={(e) => setNaturalGasCostPerMmbtu(e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: <InputAdornment position="end">/MMBtu</InputAdornment>,
                            }}
                            fullWidth
                            sx={vortxStyles.textField}
                            helperText="Used if no custom tariff selected (EIA avg: $6.00/MMBtu)"
                        />
                    )}
                </Box>
            </Box>

            {/* Gas Tariff Builder Dialog */}
            <GasTariffBuilder
                open={showGasTariffBuilder}
                onSave={handleSaveGasTariff}
                onCancel={handleClose}
            />
        </Box>
    );
}

export default FuelTariffConfiguration;
